import React, { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from './upload';
import { formatApiError } from './AuthContext';
import { resolveImageUrl } from '../lib/api';

export const ImageUploader = ({ value, onChange, label = 'Cover image', testId = 'cover-image' }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file (JPG, PNG, WEBP or GIF)'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be smaller than 10 MB'); return; }
    setProgress(0);
    try {
      const res = await uploadImage(file, setProgress);
      onChange(res.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(formatApiError(err, 'Upload failed'));
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
          <img src={resolveImageUrl(value)} alt="" className="w-full max-h-64 object-cover" data-testid={`${testId}-preview`} />
          <div className="absolute top-2 right-2 flex gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} data-testid={`${testId}-replace`} className="bg-white/90 hover:bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded-md shadow inline-flex items-center gap-1">
              <ImagePlus className="w-3.5 h-3.5" /> Replace
            </button>
            <button type="button" onClick={() => onChange('')} data-testid={`${testId}-remove`} className="bg-white/90 hover:bg-red-50 text-red-600 text-xs font-medium px-3 py-1.5 rounded-md shadow inline-flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testId}-dropzone`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
          className={`w-full rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? 'border-[#8B2C3A] bg-[#8B2C3A]/5' : 'border-gray-300 hover:border-[#8B2C3A] bg-gray-50'}`}
        >
          {progress !== null ? (
            <div className="flex flex-col items-center gap-3 text-gray-600">
              <Loader2 className="w-6 h-6 animate-spin text-[#8B2C3A]" />
              <span className="text-sm">Uploading… {progress}%</span>
              <div className="w-48 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-[#8B2C3A] transition-all" style={{ width: `${progress}%` }} /></div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-gray-600">
              <UploadCloud className="w-8 h-8 text-[#8B2C3A]" />
              <span className="text-sm font-medium">Click to upload from your computer, or drag & drop</span>
              <span className="text-xs text-gray-400">JPG, PNG, WEBP or GIF · up to 10 MB</span>
            </div>
          )}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`${testId}-input`} onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
};
